import { KeyRound, PlugZap, ShieldAlert } from 'lucide-react';
import type { StaffCapabilities } from '../../../types/staffManager';
import { cn } from '../../../utils/cn';

export type TesseractNeed = 'role.view' | 'role.edit' | 'user.view';

interface TesseractAccessNoticeProps {
  capabilities: StaffCapabilities | null;
  /** Which Tesseract permission the current surface needs. */
  needs?: TesseractNeed;
  /** A request-level error message to show instead of deriving one from capabilities. */
  error?: string | null;
  compact?: boolean;
  className?: string;
}

const NEED_LABEL: Record<TesseractNeed, string> = {
  'role.view': 'view staff teams',
  'role.edit': 'change team membership',
  'user.view': 'search Tesseract users',
};

function hasNeed(capabilities: StaffCapabilities, need: TesseractNeed): boolean {
  const t = capabilities.tesseract;
  if (need === 'role.view') return t.can_view_roster;
  if (need === 'role.edit') return t.can_edit_teams;
  return t.can_view_users;
}

/**
 * Explains exactly why a Tesseract-backed feature is unavailable: delegation not configured,
 * the session was rejected, or the user's own Tesseract account lacks the required permission.
 * Renders nothing when everything is in order.
 */
export const TesseractAccessNotice = ({ capabilities, needs, error, compact = false, className }: TesseractAccessNoticeProps) => {
  let title: string | null = null;
  let body: string | null = null;
  let Icon = ShieldAlert;

  if (error) {
    title = 'Tesseract request failed';
    body = error;
    Icon = PlugZap;
  } else if (capabilities && !capabilities.tesseract.connected) {
    const code = capabilities.tesseract.error_code;
    Icon = PlugZap;
    if (code === 'DELEGATION_NOT_CONFIGURED') {
      title = 'Tesseract link not configured yet';
      body = 'ARGUS is not yet configured to talk to Tesseract on your behalf. Team management stays read-only until the server secrets are set.';
    } else if (code === 'DELEGATION_JWT_INVALID' || code === 'DELEGATION_IDENTITY_MISMATCH') {
      title = 'Tesseract did not accept your session';
      body = 'Sign out of ARGUS and back in, then reload this page.';
    } else if (code === 'ACCOUNT_DISABLED') {
      title = 'Your Tesseract account is disabled';
      body = 'A Tesseract admin needs to re-enable it before you can manage staff.';
    } else {
      title = 'Tesseract is unavailable';
      body = capabilities.tesseract.error || 'Could not reach Tesseract. Try again in a moment.';
    }
  } else if (capabilities && needs && !hasNeed(capabilities, needs)) {
    Icon = KeyRound;
    title = `Your Tesseract account can't ${NEED_LABEL[needs]}`;
    body = `${capabilities.tesseract.username ? `@${capabilities.tesseract.username}` : 'Your account'} does not hold the Tesseract permission ${needs}. Ask a Tesseract admin to grant it — ARGUS only ever acts with your own Tesseract permissions.`;
  }

  if (!title) return null;

  return (
    <div
      role="status"
      className={cn(
        'rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-100',
        compact ? 'px-3 py-2 flex items-start gap-2' : 'p-4 md:p-5 flex items-start gap-3',
        className,
      )}
    >
      <span className={cn('inline-flex items-center justify-center rounded-lg bg-amber-500/20 text-amber-300 flex-shrink-0', compact ? 'w-6 h-6' : 'w-8 h-8')}>
        <Icon className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
      </span>
      <div className="min-w-0">
        <div className={cn('font-semibold text-amber-200', compact ? 'text-xs' : 'text-sm')}>{title}</div>
        <p className={cn('text-amber-100/80 leading-relaxed', compact ? 'text-[11px] mt-0.5' : 'text-sm mt-1')}>{body}</p>
      </div>
    </div>
  );
};
